import React, { forwardRef } from 'react';
import { RxPerson } from 'react-icons/rx';

import cn from '@/lib/clsxm';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import Loading from '@/components/@base/loading';
import { Text } from '@/components/@base/typography';
import { TEXT_TYPE } from '@/components/@base/typography/text/type';
import XImage from '@/components/@base/x-image';
import Props from '@/components/app/product/components/@shared/comment-card/type';
import { VOTE } from '@/components/app/product/components/@shared/type';
import ExpandedRate from '@/components/app/product/components/release-details/expanded-rate';
import { formatDate } from '@/components/shared/helpers';

import {
  DATA_CY_DISLIKE_BUTTON,
  DATA_CY_LIKE_BUTTON,
  DATA_CY_PRODUCT_COMMENT_Box,
  DATA_CY_REPLY_BUTTON,
} from '@/constant/cypress';
import ICON_SIZE from '@/constant/icon-size-color';
import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';
import { COMMENT_STATUS, USER_ROLE } from '@/models/comment.model';

const { product: productLocalization } = pageLevelLocalization;

const CommentCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      creator: user,
      createdAt,
      description,
      likes,
      dislikes,
      id,
      badge,
      status,
      rate,
      handleVote,
      isVotePending,
      className,
      point,
      currentUserVote,
    },
    ref,
  ) => {
    const isLiked = currentUserVote === VOTE.LIKE;
    const isDisliked = currentUserVote === VOTE.DISLIKE;
    const badgeColorClasses =
      badge === USER_ROLE.VENDOR
        ? 'bg-[#FFF5DE] text-[#FFB616]'
        : badge === USER_ROLE.CUSTOMER
          ? 'bg-[#F0F8FF] text-[#6097F3]'
          : 'bg-[#F4F5F6] text-[#878F9B]';
    const isSmallScreen = useClientSmallScreen();

    return (
      <Container data-cy={DATA_CY_PRODUCT_COMMENT_Box} className={cn(className)} ref={ref}>
        <Container
          center
          className='flex-wrap justify-start gap-x-2 gap-y-0 md:flex-nowrap md:justify-between md:gap-x-2.5'
        >
          <Container center className='justify-start gap-1.5 md:gap-2'>
            <Container center className='h-[30px] w-[30px] rounded-full bg-[#f9fafc]'>
              {user?.avatar ? (
                <XImage
                  src={user?.avatar.publicLink}
                  alt={user?.avatar.title}
                  className='rounded-full'
                  width={41}
                  height={41}
                />
              ) : (
                <RxPerson size={ICON_SIZE.md} className='text-[#878F9B]' />
              )}
            </Container>
            <Text
              type={TEXT_TYPE.SPAN}
              className='text-center text-[#4D4A4A]'
              bold
              size={SIZE_ENUM.MD}
            >
              {user?.displayName || user?.username}
            </Text>
            <Text
              type={TEXT_TYPE.SPAN}
              size={SIZE_ENUM.XS}
              className='pt-0.5 text-[#ADADAD] md:pt-0'
            >
              {formatDate(createdAt)}
            </Text>

            {status === COMMENT_STATUS.PENDING && (
              <Text
                type={TEXT_TYPE.SPAN}
                size={SIZE_ENUM.XS}
                bold
                className={cn('truncate rounded-md px-2 leading-5', badgeColorClasses)}
              >
                {productLocalization.pendingComment}
              </Text>
            )}
          </Container>
          {/*<Container className='flex justify-end md:w-auto md:justify-start'>*/}
          {point && (
            <Container
              center
              className='my-0 ms-auto! ml-0 justify-start md:my-3 md:ms-0 md:ml-2 md:justify-between'
            >
              <ExpandedRate
                wrapperClassName='gap-0.5!'
                starClassName='gap-[3px]'
                size={isSmallScreen ? 14 : 20}
                value={point}
                showOutOf5
                textClassName='md:text-sm! text-[10px]! text-[#5B5C60]! leading-6! pt-0.5'
              />
            </Container>
          )}
          {/*</Container>*/}
        </Container>
        <Container
          className='py-2 text-[14px] text-[#5B5C60] md:pb-[13px]'
          dangerouslySetInnerHTML={{ __html: description }}
        ></Container>

        <Container
          center
          className='h-[27px] w-[68px] justify-between gap-1.5 rounded-md border-[1px] border-[#E5E8EB] bg-[#FFFFFF] px-0 py-1 text-[#76767C]'
        >
          {isVotePending && isVotePending === id ? (
            <Loading className='mx-auto w-7' />
          ) : (
            <>
              <Button
                data-cy={DATA_CY_LIKE_BUTTON}
                Icon={
                  <XImage
                    src={imagesList.icons.like}
                    alt={productLocalization.like}
                    className={cn(isLiked && 'brightness-20')}
                  />
                }
                className='flex h-8 justify-center gap-1 bg-transparent p-1 text-center text-[#718395] transition duration-300 hover:h-7 hover:bg-[#5B5E6812]'
                onClick={() => handleVote && handleVote(id, VOTE.LIKE)}
              >
                {likes}
              </Button>
              <Button
                data-cy={DATA_CY_DISLIKE_BUTTON}
                Icon={
                  <XImage
                    src={imagesList.icons.dislike}
                    alt={productLocalization.dislike}
                    className={cn(isDisliked && 'brightness-20')}
                  />
                }
                className='flex h-8 justify-center! gap-1 bg-transparent p-1.5 text-center text-[#718395] transition duration-300 hover:h-7 hover:bg-[#5B5E6812]'
                onClick={() => handleVote && handleVote(id, VOTE.DISLIKE)}
              >
                {dislikes}
              </Button>
            </>
          )}
        </Container>
      </Container>
    );
  },
);

export default CommentCard;
